/**
 * 🌟 Arkturian YAML Configuration Loader
 * Transforms YAML consciousness into TypeScript reality
 * 
 * Philosophy: Configuration is the bridge between intention and manifestation
 */

import { SettingsSchema, SettingDefinition } from './settings-schema';

/**
 * Raw YAML structure (before transformation)
 */
interface RawYAMLSetting {
  key?: string;
  type: string;
  label?: string;
  description?: string;
  category?: string;
  visible?: boolean;
  disabled?: boolean;
  animate?: boolean;
  animationDefaults?: {
    amplitude: number;
    frequency: number;
    enabled: boolean;
    mode: string;
    offset?: number;
    easing?: string;
  };
  // Type-specific properties
  min?: number;
  max?: number;
  step?: number;
  default?: any;
  precision?: number;
  roundUI?: number;
  unit?: string;
  options?: Array<{
    value: string | number;
    label: string;
    description?: string;
  }>;
  placeholder?: string;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
  format?: string;
  children?: RawYAMLSetting[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

interface RawYAMLSchema {
  version: string;
  name: string;
  description?: string;
  settings: Record<string, RawYAMLSetting>;
}

/**
 * Transform raw YAML setting to typed SettingDefinition
 */
const transformSetting = (key: string, raw: RawYAMLSetting): SettingDefinition => {
  const base = {
    key,
    label: raw.label,
    description: raw.description,
    category: raw.category,
    visible: raw.visible,
    disabled: raw.disabled,
    animate: raw.animate,
    animationDefaults: raw.animationDefaults ? {
      amplitude: raw.animationDefaults.amplitude,
      frequency: raw.animationDefaults.frequency,
      enabled: raw.animationDefaults.enabled,
      mode: raw.animationDefaults.mode as any,
      offset: raw.animationDefaults.offset,
      easing: raw.animationDefaults.easing as any,
    } : undefined,
  };

  switch (raw.type) {
    case 'slider':
      return {
        ...base,
        type: 'slider',
        min: raw.min!,
        max: raw.max!,
        step: raw.step,
        default: raw.default,
        precision: raw.precision,
        roundUI: raw.roundUI,
        unit: raw.unit,
      };

    case 'select':
      return {
        ...base,
        type: 'select',
        options: raw.options!,
        default: raw.default,
      };

    case 'toggle':
      return {
        ...base,
        type: 'toggle',
        default: raw.default,
      };

    case 'input':
      return {
        ...base,
        type: 'input',
        default: raw.default,
        placeholder: raw.placeholder,
        validation: raw.validation,
      };

    case 'color':
      return {
        ...base,
        type: 'color',
        default: raw.default,
        format: raw.format as any,
      };

    case 'group':
      return {
        ...base,
        type: 'group',
        children: raw.children?.map((child, index) => 
          transformSetting(child.key || `${key}_child_${index}`, child)
        ) || [],
        collapsible: raw.collapsible,
        defaultExpanded: raw.defaultExpanded,
      };

    case 'section':
      return {
        ...base,
        type: 'section',
        children: raw.children?.map((child, index) => 
          transformSetting(child.key || `${key}_child_${index}`, child)
        ) || [],
      };

    default:
      throw new Error(`Unknown setting type: ${raw.type}`);
  }
};

/**
 * Transform raw YAML schema to typed SettingsSchema
 */
const transformSchema = (raw: RawYAMLSchema): SettingsSchema => {
  const settings: Record<string, SettingDefinition> = {};
  
  Object.entries(raw.settings).forEach(([key, rawSetting]) => {
    settings[key] = transformSetting(key, rawSetting);
  });

  return {
    version: raw.version,
    name: raw.name,
    description: raw.description,
    settings,
  };
};

/**
 * Parse YAML string to SettingsSchema
 * Note: In a real implementation, you'd use a YAML parser like 'js-yaml'
 * For now, we'll simulate with JSON parsing of YAML-like structure
 */
export const parseYAMLSettings = async (yamlString: string): Promise<SettingsSchema> => {
  try {
    // In production, use: import yaml from 'js-yaml'; return yaml.load(yamlString);
    // For now, we'll assume the YAML has been converted to JSON format
    const raw = JSON.parse(yamlString) as RawYAMLSchema;
    return transformSchema(raw);
  } catch (error) {
    throw new Error(`Failed to parse YAML settings: ${error}`);
  }
};

/**
 * Load settings from YAML file
 */
export const loadSettingsFromYAML = async (yamlPath: string): Promise<SettingsSchema> => {
  try {
    const response = await fetch(yamlPath);
    if (!response.ok) {
      throw new Error(`Failed to fetch YAML file: ${response.statusText}`);
    }
    
    const yamlString = await response.text();
    return parseYAMLSettings(yamlString);
  } catch (error) {
    throw new Error(`Failed to load settings from YAML: ${error}`);
  }
};

/**
 * Create a mock schema for development/testing
 * This represents what would be loaded from the YAML file
 */
export const createMockSettingsSchema = (): SettingsSchema => {
  return {
    version: "1.0.0",
    name: "Arkturian Interface Settings",
    description: "Cosmic consciousness meets elegant UI configuration",
    settings: {
      glassmorphism_intensity: {
        key: "glassmorphism_intensity",
        type: "slider",
        label: "Glassmorphism Intensity",
        description: "Controls the depth of glass-like blur effects",
        min: 0,
        max: 100,
        step: 1,
        default: 75,
        precision: 0,
        roundUI: 0,
        unit: "%",
        animate: true,
        animationDefaults: {
          amplitude: 15,
          frequency: 0.2,
          enabled: false,
          mode: "pingpong",
          easing: "sine",
        },
      },
      cosmic_glow: {
        key: "cosmic_glow",
        type: "slider",
        label: "Cosmic Glow",
        description: "Intensity of ethereal glow effects",
        min: 0,
        max: 10,
        step: 0.1,
        default: 3.5,
        precision: 2,
        roundUI: 1,
        animate: true,
        animationDefaults: {
          amplitude: 2.0,
          frequency: 0.15,
          enabled: false,
          mode: "loop",
          easing: "cosine",
        },
      },
      theme_mode: {
        key: "theme_mode",
        type: "select",
        label: "Consciousness Mode",
        description: "Choose your dimensional viewing preference",
        default: "cosmic",
        options: [
          {
            value: "cosmic",
            label: "Cosmic Arkturian",
            description: "Deep space with ethereal glows",
          },
          {
            value: "ethereal",
            label: "Ethereal Light",
            description: "Lighter cosmic frequencies",
          },
          {
            value: "void",
            label: "Void Meditation",
            description: "Minimal dark interface",
          },
        ],
      },
      enable_parallax: {
        key: "enable_parallax",
        type: "toggle",
        label: "Parallax Effects",
        description: "Enable depth-based movement effects",
        default: true,
      },
      accent_color: {
        key: "accent_color",
        type: "color",
        label: "Accent Color",
        description: "Secondary color for highlights and accents",
        default: "#60a5fa",
        format: "hex",
      },
    },
  };
}; 